<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            added: "Ajouté",
            per_month: "/ mois",
            per_year: "/ an",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Nom de l'offre */
        name: string
        /** Description de l'offre */
        description?: string
        /** Prix */
        price: number
        /** Prix barré (promo) */
        discountPrice?: number | null
        /** Liste des features */
        features?: string[]
        /** Sélectionné */
        selected?: boolean
        /** Lecture seule (non cliquable) */
        readonly?: boolean
        /** Badge mis en avant (ex. "Le plus choisi") */
        badge?: string
        /** Pastille sur le prix (ex. économie engagement) */
        priceBadge?: string
        /** Unité du prix affiché : mois (défaut) ou an (engagement). */
        priceUnit?: "month" | "year"
        /** Prix mensuel (affiché en petit sous le prix principal quand priceUnit === 'year'). */
        pricePerMonth?: number
    }>(),
    {
        description: "",
        features: () => [],
        selected: false,
        readonly: false,
        priceUnit: "month",
        pricePerMonth: undefined,
    }
)

const emit = defineEmits<{
    (e: "click", value: boolean): void
}>()

// ================================
// HELPERS
// ================================
/** Formate le prix en euros */
function formatPrice(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value)
}

// ================================
// COMPUTED
// ================================
/** Prix affiché (promo si définie) */
const getDisplayPrice = computed(() =>
    props.discountPrice != null
        ? formatPrice(props.discountPrice)
        : formatPrice(props.price)
)

/** Prix barré (si promo) */
const getStrikethroughPrice = computed(() =>
    props.discountPrice != null ? formatPrice(props.price) : null
)

/** Unité du prix (mois ou an). */
const getPriceUnitLabel = computed(() =>
    props.priceUnit === "year" ? t("per_year") : t("per_month")
)

/** Prix mensuel formaté (affiché en petit sous le prix principal quand engagement). */
const getPricePerMonthFormatted = computed(() =>
    props.pricePerMonth != null ? formatPrice(props.pricePerMonth) : null
)

// ================================
// HANDLERS
// ================================
function onClick(): void {
    if (!props.readonly) {
        emit("click", !props.selected)
    }
}
</script>

<template>
    <!-- Carte d'offre (sélectionnable) -->
    <div
        class="flex flex-col justify-start items-center gap-4 p-4 rounded-lg border transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        :class="[
            selected
                ? 'border-primary-300 bg-primary-100 ring-1 ring-primary-200'
                : 'border-primary-200 bg-primary-50/50 hover:border-primary-300 hover:bg-primary-100/80',
            readonly &&
                'cursor-default hover:border-primary-200 hover:bg-primary-50/50',
        ]"
        role="button"
        :aria-pressed="selected"
        tabindex="0"
        @click="onClick"
        @keydown.enter.space.prevent="onClick"
    >
        <div class="flex flex-col gap-2 self-stretch">
            <div class="flex flex-wrap items-center gap-2 px-2">
                <span
                    v-if="badge"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                >
                    {{ badge }}
                </span>
                <h3 class="text-2xl font-semibold text-primary-900">
                    {{ name }}
                </h3>
                <span
                    v-if="selected"
                    class="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 ml-auto"
                >
                    <BaseIcon icon="pi pi-check" class="shrink-0" />
                    {{ t("added") }}
                </span>
            </div>
            <p v-if="description" class="text-sm text-primary-700 px-2">
                {{ description }}
            </p>
            <div class="flex flex-col gap-1 px-2">
                <p class="flex items-end gap-2 flex-wrap">
                    <span
                        v-if="getStrikethroughPrice"
                        class="text-base text-neutral-600 line-through"
                    >
                        {{ getStrikethroughPrice }}
                    </span>
                    <span class="text-2xl font-bold text-primary-900">{{
                        getDisplayPrice
                    }}</span>
                    <span class="text-primary-500">{{
                        getPriceUnitLabel
                    }}</span>
                    <span
                        v-if="getPricePerMonthFormatted"
                        class="text-sm text-primary-600"
                    >
                        ({{ getPricePerMonthFormatted }} {{ t("per_month") }})
                    </span>
                </p>
                <div
                    v-if="priceBadge"
                    class="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-green-100 text-green-800 w-fit"
                >
                    <span>{{ priceBadge }}</span>
                </div>
            </div>
        </div>
        <ul
            v-if="features?.length"
            class="flex flex-col gap-1 self-stretch list-none p-0 m-0"
        >
            <li
                v-for="(feature, index) in features"
                :key="`feature-${index}`"
                class="flex items-start gap-2 px-2 py-1 text-sm"
                :class="selected ? 'text-green-700' : 'text-neutral-700'"
            >
                <BaseIcon icon="pi pi-check" class="shrink-0 mt-0.5" />
                <span>{{ feature }}</span>
            </li>
        </ul>
        <slot name="extra" />
    </div>
</template>
